import { apiFetch } from "./client";
import type {
  TaskByClientResponse,
  TaskCreatePayload,
  TaskDetailResponse,
  TaskEditPayload,
  TaskListQuery,
  TaskListResponse,
  TaskPostponePayload,
  TaskScope,
} from "./types";
import type { Client360TaskCompletePayload } from "./client360Tasks";

export function fetchTasks(
  q: TaskListQuery = {},
  signal?: AbortSignal,
): Promise<TaskListResponse> {
  return apiFetch<TaskListResponse>("/Custom/frontend/tasks", {
    query: {
      scope: q.scope,
      view: q.view,
      cursor: q.cursor,
      limit: q.limit,
      includeRecentCompleted: q.includeRecentCompleted,
      teamFilter: q.teamFilter,
      byManager: q.byManager,
    },
    signal,
  });
}

/** v1.2 §6.4 — «По клієнтах» holding-grouped active tasks. */
export function fetchTasksByClient(
  scope: TaskScope = "my",
  signal?: AbortSignal,
): Promise<TaskByClientResponse> {
  return apiFetch<TaskByClientResponse>("/Custom/frontend/tasks/by-client", {
    query: { scope },
    signal,
  });
}

export function fetchTaskDetail(
  id: string,
  signal?: AbortSignal,
): Promise<TaskDetailResponse> {
  return apiFetch<TaskDetailResponse>(
    `/Custom/frontend/tasks/${encodeURIComponent(id)}`,
    { signal },
  );
}

export function createTask(body: TaskCreatePayload): Promise<TaskDetailResponse> {
  return apiFetch<TaskDetailResponse>("/Custom/frontend/tasks", {
    method: "POST",
    body,
  });
}

export function editTask(
  id: string,
  body: TaskEditPayload,
): Promise<TaskDetailResponse> {
  return apiFetch<TaskDetailResponse>(
    `/Custom/frontend/tasks/${encodeURIComponent(id)}`,
    { method: "PATCH", body },
  );
}

/**
 * v1.2 R1 — F3 complete carries the SAME outcome body as the 3A endpoint
 * (canonical TaskCompleteModal). Response stays the bare TaskDetailResponse.
 */
export function completeTask(
  id: string,
  body: Client360TaskCompletePayload | Record<string, never> = {},
): Promise<TaskDetailResponse> {
  return apiFetch<TaskDetailResponse>(
    `/Custom/frontend/tasks/${encodeURIComponent(id)}/complete`,
    { method: "POST", body },
  );
}

export function reopenTask(id: string): Promise<TaskDetailResponse> {
  return apiFetch<TaskDetailResponse>(
    `/Custom/frontend/tasks/${encodeURIComponent(id)}/reopen`,
    { method: "POST", body: {} },
  );
}

export function postponeTask(
  id: string,
  body: TaskPostponePayload,
): Promise<TaskDetailResponse> {
  return apiFetch<TaskDetailResponse>(
    `/Custom/frontend/tasks/${encodeURIComponent(id)}/postpone`,
    { method: "POST", body },
  );
}

/** D-142.W1 (П-10) — server-owned пошук кандидатів у спостерігачі (active-only). */
export interface WatcherOptionsResponse {
  options: Array<{ id: string; name: string }>;
}

export function fetchWatcherOptions(
  q: string,
  signal?: AbortSignal,
): Promise<WatcherOptionsResponse> {
  return apiFetch<WatcherOptionsResponse>(
    "/Custom/frontend/tasks/watcher-options",
    { query: { q: q || undefined }, signal },
  );
}
